import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTitle } from "../useTitle";
import { ApiError, api, setCsrf } from "../api";

/**
 * Shown instead of the app when the session still holds the temporary password.
 *
 * Instead of, not alongside: a banner is dismissible and a form you can navigate
 * away from is a form nobody fills in. The server refuses every write from such
 * a session anyway, so letting the app render would mean a UI where each button
 * fails with a 403 - which reads as broken rather than as "do this first".
 */
export default function ChangePassword({
  onChanged,
  minLength = 10,
}: {
  onChanged: () => void;
  minLength?: number;
}) {
  const { t } = useTranslation();
  useTitle(t("password.title"));
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Checked here so the reason appears as you type, and checked again on the
  // server because a client-side rule is a hint, not a control.
  const tooShort = next.length > 0 && next.length < minLength;
  const mismatch = again.length > 0 && next !== again;
  const same = next.length > 0 && next === current;
  const ready = next.length >= minLength && next === again && !same && !!current;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.changePassword(current, next);
      setCsrf(r.csrf);
      setError("");
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-slate-50 p-4 dark:bg-slate-950">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
      >
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t("password.heading")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {t("password.intro")}
        </p>

        <Field
          label={t("password.current")}
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
          autoFocus
        />
        <Field
          label={t("password.next", { count: minLength })}
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          hint={
            tooShort
              ? t("password.tooShort", { count: minLength - next.length })
              : same
                ? t("password.same")
                : ""
          }
        />
        <Field
          label={t("password.again")}
          value={again}
          onChange={setAgain}
          autoComplete="new-password"
          hint={mismatch ? t("password.mismatch") : ""}
        />

        {error && (
          <p className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !ready}
          className="mt-4 w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900"
        >
          {busy ? t("common.saving") : t("password.submit")}
        </button>
      </form>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">) {
  return (
    <label className="mt-4 block text-sm">
      <span className="mb-1 block text-slate-600 dark:text-slate-400">{label}</span>
      <input
        {...rest}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
      />
      {hint && <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">{hint}</span>}
    </label>
  );
}
